import React, { useState, useEffect } from 'react';
import { Box, Button, useTheme } from '@mui/material';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { reduxHelper } from '../../../config/reduxHelper';
import TextPropertiesPanel from '../../siteBuilder/properties/TextPropertiesPanel';

const FlexibleButtonBlock = ({ block, isSiteBuilder, isActive, handleUpdate, pageFontSx, onDelete, onDuplicate }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { push } = useRouter();
    const { siteBuilderSelectedChild } = useSelector((s) => s.reducer);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        if (isSiteBuilder && isActive && siteBuilderSelectedChild?.uid !== block.uid) {
            dispatch({
                type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD,
                payload: { ...block, type: 'button' },
            });
        }
    }, [isActive, isSiteBuilder, block, siteBuilderSelectedChild, dispatch]);

    const outlineColor = theme.palette.info.main;

    const handleClick = (e) => {
        if (isSiteBuilder) {
            e.stopPropagation();
            return;
        }

        if (block.url) {
            if (typeof block.url === 'string') {
                if (block.url.startsWith('http')) {
                    window.open(block.url, '_blank', 'noopener,noreferrer');
                } else {
                    push(block.url);
                }
            } else if (typeof block.url === 'object' && block.url.path) {
                if (block.url.target) {
                    window.open(block.url.path, '_blank', 'noopener,noreferrer');
                } else {
                    push(block.url.path);
                }
            }
        }
    };

    // Calculate justifyContent based on alignment props
    const getJustifyContent = () => {
        if (block.props?.align === 'left') return 'flex-start';
        if (block.props?.align === 'right') return 'flex-end';
        if (block.props?.align === 'center') return 'center';

        // Fallback to sx check
        if (block.props?.sx?.marginLeft === 0 && block.props?.sx?.marginRight === 'auto') return 'flex-start';
        if (block.props?.sx?.marginLeft === 'auto' && block.props?.sx?.marginRight === 0) return 'flex-end';
        return 'center';
    };

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: getJustifyContent(),
                width: '100%'
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {isSiteBuilder && isActive && (
                <Box
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 10000,
                        mb: 1
                    }}
                >
                    <TextPropertiesPanel
                        childOverride={block}
                        onChangeChild={(updated) => handleUpdate(updated)}
                        onDeleteChild={() => onDelete && onDelete(block.id)}
                        onDuplicateChild={() => onDuplicate && onDuplicate(block.id)}
                    />
                </Box>
            )}

            <Button
                variant={block.props?.variant || 'contained'}
                onClick={handleClick}
                disableElevation
                sx={{
                    textTransform: 'none',
                    minWidth: 100,
                    borderRadius: block.props?.sx?.borderRadius || '8px',
                    fontWeight: block.props?.style?.fontWeight || 600,
                    fontSize: block.props?.style?.fontSize || '0.9rem',
                    ...pageFontSx,
                    ...(block.props?.variant === 'outlined' || block.props?.variant === 'text'
                        ? { backgroundColor: 'transparent' }
                        : { backgroundColor: block.props?.sx?.backgroundColor || theme.palette.primary.main }
                    ),
                    ...block.props?.sx,
                    // Ensure variant-specific properties override generic sx if needed
                    ...(block.props?.variant === 'outlined' ? { backgroundColor: 'transparent' } : {}),
                    ...(block.props?.variant === 'text' ? { backgroundColor: 'transparent', border: 'none' } : {}),
                    ...block.props?.style,
                    '&:hover': {
                        ...block.props?.sx?.['&:hover'],
                        opacity: 0.9,
                        transform: (isSiteBuilder || !block.url) ? 'none' : 'translateY(-1px)',
                    }
                }}
            >
                <Box
                    contentEditable={isSiteBuilder}
                    suppressContentEditableWarning
                    onBlur={(e) => {
                        const val = e.currentTarget.innerText;
                        handleUpdate({
                            value: val,
                            label: val,
                            children: val
                        });
                    }}
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isSiteBuilder ? 'text' : 'pointer',
                        userSelect: isSiteBuilder ? 'text' : 'none',
                        outline: 'none',
                    }}
                >
                    {block.label || 'Button'}
                </Box>
            </Button>
        </Box>
    );
};

export default FlexibleButtonBlock;
